import { Button, Dialog } from '@startlibs/components';
import {Fill, useEnsureRef, useRefState, useToggle} from '@startlibs/core';
import { getColor, postFetcher } from '@startlibs/utils';
import { lighten } from 'polished';
import { useNavigate } from '@startlibs/components';
import { FormattedMessage, useIntl } from 'react-intl';
import React, {useContext, useEffect, useRef, useState} from 'react'
import load from 'little-loader'
import styled, {createGlobalStyle} from 'styled-components';
import {Card, PageContainer, PageFooter, SectionHeading} from '../components/PageLayout'
import {CategoriesBox, lazyPublicCategories} from '../admin/experts/hooks/useEditCategories'
import {ConfirmDialog, useConfirmDialog} from '../hooks/useConfirmDialog'
import {Header} from '../components/Header'
import {LogoutButton} from '../components/Navbar';
import {OptionalHeading} from '../request/forms/InsuranceDetails'
import {PENDING_REQUEST} from '../enums/CaseState'
import {PurviewFooter} from '../components/PurviewFooter'
import {ServiceTermsContent} from './PatientSignTerms'
import {useUrgentNoticeDismissed} from '../pages/LandingPage'
import {createCaseTransform} from '../request/utils/createCaseUtils'
import {getTimezoneOffset} from '../utils/utils'
import {responseFailure} from '../utils/validation'
import {setJwt} from '../hooks/useJwt'
import {willUseSuspense} from '../hooks/useSuspense'
import { convertToCareQualityGender } from '../enums/GenderCarequality';
import { jwtGetFetcher, jwtPostFetcher } from '../utils/authFetch';
import { CountryRegionData } from 'react-country-region-selector';
import { buildIntakeFormsSteps, getPathForTab, STEP_CASE_SETTINGS, TAB_TYPE_CUSTOM } from '../request/forms/utils';
import { PatientAndContactDetailsNew } from '../request/PatientAndContactDetailsNew';
import { HasErrorsLabel, PanelStepsAndForm, Step, StepsWrapper } from '../admin/AdminCreateRequestNew';
import { IfLocale, useIsLocale } from '../hocs/IfLocale';
import { PATIENT } from '../enums/UserRoles';
import { getRelationLabel } from '../enums/ContactRelationKind';
import { setNotification } from '../components/Notifications';
import { LocaleContext } from '../components/I18NProvider';
import { LocaleSelector } from '../components/LocaleSelector';
import { getTranslatedTitles, INSURANCE_INFORMATION, PHYSICIAN } from '../admin/controlPanel/intakeUtils';
import { LoadingPage } from '../components/PageLoading';
import { defaultReformatDate } from '../utils/dateFormat';


const GENDER_LABELS = (intl) => ({
  M: intl.formatMessage({defaultMessage:"Male",description:"Gender label: male"}),
  F: intl.formatMessage({defaultMessage:"Female",description:"Gender label: female"}),
  O: intl.formatMessage({defaultMessage:"Gender: other",description:"Gender label: other"}),
  N: intl.formatMessage({defaultMessage:'Gender: Non-binary',description:"Gender label: non-binary"})
})

// Create a more robust captcha loading mechanism
let captchaPromise = null
let captchaResolver = null

const getCaptchaPromise = () => {
  if (!captchaPromise) {
    captchaPromise = new Promise((resolve, _reject) => {
      captchaResolver = resolve
      // Set up the global callback that reCAPTCHA will call
      window.onLoadCaptchResolve = () => {
        resolve()
      }
    })
  }
  return captchaPromise
}

const useLoadCaptcha = willUseSuspense((captchaEnabled) => {
  if (!captchaEnabled) {
    return Promise.resolve()
  }
  
  // Check if reCAPTCHA is already loaded
  if (window.grecaptcha && window.grecaptcha.render) {
    return Promise.resolve()
  }
  
  load("https://www.google.com/recaptcha/api.js?onload=onLoadCaptchResolve&render=explicit")
  return getCaptchaPromise()
})

const HideRecaptha = createGlobalStyle`
.grecaptcha-badge { visibility: hidden; }
`

export const RecaptchaBrandingText = styled.div`
  color: rgba(0,0,0,0.4);
  font-size: 12px;
  margin-right: auto;
  padding-right: 1rem;
  align-self: center;
  a {
    color: inherit;
  }
`

export const EmergencyDisclaimer = styled.div `
  margin: -1rem 0 1.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: ${props => lighten(0.48, props.theme.colors.alert)};
  border: 1px solid ${props => lighten(0.4, props.theme.colors.alert)};
  color: ${getColor('alert')};
`

const CasePreview = styled.div`
    margin-top: -1.75rem;
    h4 {
      margin-top: 2.5rem;
      border-bottom: 1px solid ${getColor('gray230')};
      padding-bottom: 0.5rem;
    }
    p {
      margin-bottom: 0.5rem;
    }
`

export const PatientCreateRequestNew = ({setCaseRequest, isNurse, isRefPhysician, providerInfo, onChange, formRef, markedAsPriority}) => {

  const navigate = useNavigate()
  const intl = useIntl()

  const urlType = (!isNurse && !isRefPhysician) ? 'patient' : isNurse ? 'nurse' : 'refphysician'

  const isMultiLanguage = providerInfo?.preferredLanguage?.options && providerInfo?.preferredLanguage?.options?.length > 1
  
  const ensuredFormRef = useEnsureRef(formRef)

  const captchaEnabled = providerInfo.captchaEnabled

  useLoadCaptcha(captchaEnabled)

  const captchaRef = useRef()
  const submitValues = useRefState()
  const isLegal = useIsLocale({contains:'LEGAL'})
  const [isPreview, setIsPreview] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedStepNumber, setSelectedStepNumber] = useState(0)
  const [refresh, setRefresh] = useState(0)
  const [isLoadingDefaultForm, setIsLoadingDefaultForm] = useState(true)
  const categories = useToggle([])

  const {locale} = useContext(LocaleContext)

  const careQualityEnabled = providerInfo?.carequalityEnabled && !intl.locale.indexOf("LEGAL") >= 0

  const [steps, setSteps ] = useState([])
  const stepsWrapperRef = useRef(null);
  useEffect(() => {
    if (stepsWrapperRef.current && selectedStepNumber !== null) {
      const container = stepsWrapperRef.current;
      const stepIndex = steps.findIndex(step => step.order === selectedStepNumber);
      const stepElement = container.children[stepIndex];

      if (stepElement) {
        const containerWidth = container.offsetWidth;
        const stepWidth = stepElement.offsetWidth;
        const stepOffsetLeft = stepElement.offsetLeft;

        // Calculate target scroll position
        const targetScrollPosition = stepOffsetLeft - (containerWidth / 2 - stepWidth / 2);

        if (stepIndex === 0) {
          // Align the first step to the start
          container.scrollTo({ left: 0, behavior: 'smooth' });
        } else if (stepIndex === steps.length - 1) {
          // Align the last step to the end
          const lastStepVisiblePosition = container.scrollWidth - containerWidth;
          container.scrollTo({ left: lastStepVisiblePosition, behavior: 'smooth' });
        } else {
          // Center the clicked step
          container.scrollTo({ left: targetScrollPosition, behavior: 'smooth' });
        }
      }
    }
    if (selectedStepNumber === 0 && steps.length > 0) {
      const firstStep = steps[0];
      if (firstStep.number !== selectedStepNumber) {
        setSelectedStepNumber(firstStep.number);
      }
    }
  }, [selectedStepNumber, steps]);

  const captchaCallback = async (captchaToken) => postFetcher('/api/createCaseRequest',
  await createCaseTransform({
    referringPhysician: {},
    ...submitValues.get().values,
    byNurse: isNurse,
    byPhysician: isRefPhysician,
    captchaToken
  },intl.locale.indexOf("LEGAL") >= 0))
    .then(submitValues.get().res, submitValues.get().rej)

  useEffect(() => {
    if (captchaEnabled) {
      window.grecaptcha.ready(function () {
        window.grecaptcha.render(captchaRef.current, {
          "sitekey": "6LeBfaUZAAAAAEQNeD7e0EcudKBnZJnuZAqGAZ49",
          "size": "invisible",
          callback: captchaCallback
        });
      });
    }
  }, [])

  useEffect(() => {

    const generateDefaultForm = () => {
      const intakeFormsSteps = buildIntakeFormsSteps({
        language: providerInfo?.preferredLanguage?.defaultLanguage,
        isAdmin: false,
        caseReconciliationEnabled: false,
        isNurse: isNurse,
      })
      setSteps(intakeFormsSteps)
    }

    jwtGetFetcher('')('/api/intakeFormTemplates/default')
      .then((data) => {
        const tabsWithOrderAndNumber = data.intakeForm.tabs.map((tab, index) => { return {...tab, order: index, number: index, formPath: getPathForTab(tab.id)} })
        const activeTabs = tabsWithOrderAndNumber.filter(tab => tab.active)
        const activeTabsAndCustomTabsWithLines = activeTabs.filter(tab => tab.type !== TAB_TYPE_CUSTOM || (tab.lines && tab.lines.length > 0))
        if(activeTabsAndCustomTabsWithLines.length === 0) {
          generateDefaultForm()
        }else{
          if(isLegal) {
            const legalTabs = activeTabsAndCustomTabsWithLines.filter(tab => tab.id !== INSURANCE_INFORMATION && tab.id !== PHYSICIAN)
            setSteps(legalTabs)
          }else{
            const tabsWithAdditionalTabs = activeTabsAndCustomTabsWithLines.concat(
              isNurse ? [
                {
                  order: 99,
                  number: 99,
                  label: "Case settings",
                  id: STEP_CASE_SETTINGS,
                  pageTitle: "Case settings",
                  pageSubTitle: "Manage the divisions that can access this case",
                  formPath: "categories"
                },
              ] : []
            )
            setSteps(tabsWithAdditionalTabs)
          }
          
        }
        
      })
      .catch((e) => {
        console.error(e)
        generateDefaultForm()
      })
      .finally(() => setIsLoadingDefaultForm(false))

  }, [])

  const discardRequest = useConfirmDialog(
    <ConfirmDialog
      // title="Confirm discard request"
      title={intl.formatMessage({
        defaultMessage: "Confirm discard request",
        description: "Discard request confirmation title"
      })}
      navigate="/access"
      confirm={<Button alert><FormattedMessage defaultMessage="Discard request" description="Discard request button" /></Button>}
    >
      <p><FormattedMessage defaultMessage="You are about to discard this request and the provided information." description="Discard request confirmation line 1" /></p>
      <p><FormattedMessage defaultMessage="Are you sure you want to discard? Once confirmed it can not be undone." description="Discard request confirmation line 2" /></p>
    </ConfirmDialog>
  )

  const action = (values) => new Promise((res, rej) => {
    submitValues.set({values, res, rej})
    if (captchaEnabled) {
      window.grecaptcha.execute()
      const iframe = document.querySelector("iframe[title='recaptcha challenge']")
      if (!iframe?.parentNode?.parentNode) {
        return;
      }
      const container = iframe.parentNode.parentNode;
      const observer = new MutationObserver(_mutations => {
        if (container && container.style.visibility === 'hidden') {
          if (!window.grecaptcha.getResponse()) {
            rej([values, {}])
          }
          observer.disconnect()
        }
      });
      observer.observe(container, {attributes: true, attributeFilter: ['style']});
    } else {
      captchaCallback()
    }
  })

  const onSuccess = (values, {jwt, requestId, requestCode, acceptanceInfo}) => {
    // Fullstory disabled
    // window.FS.setUserVars({
    //   displayName: requestCode,
    //   requestCode : requestCode,
    //   requestId: requestId,
    //   type: "PATIENT",
    //   customer: providerInfo.name
    // })
    // var now = new Date()
    // var eventProperties = {
    //   customer_str: providerInfo.name,
    //   dateTime_date: now,
    //   requestId_int: requestId,
    //   caseId_str: requestCode
    // }
    // window.FS.event('Patient Case Created',eventProperties)
    setCaseRequest({
      ...values,
      newlyCreated: true,
      needReleases: true,
      payments: [],
      caseFlags: {requestFlags: {}},
      requestId: requestId,
      requestCode: requestCode,
      state: PENDING_REQUEST,
      acceptanceInfo: acceptanceInfo || values.acceptanceInfo || {}
    })
    setJwt(jwt)

    if(careQualityEnabled){

      const birthDate = (() => {
          const dob = values?.patientInfo?.dob; // Assuming dob is in a proper string format like "YYYY-MM-DD"
          if (!dob) return null; // Handle null or undefined
          try {
              // Ensure the date format is compatible across all browsers
              const parsedDate = new Date(dob.replace(/-/g, '/')); // Replace hyphens with slashes for Safari compatibility
              return parsedDate.toISOString().split('T')[0];
          } catch (error) {
              console.error('Invalid Date Format:', error);
              return null;
          }
      })();

      
      const givenName = values?.patientInfo?.firstName.split(' ').filter(m => m.length > 0)
        .concat(values?.patientInfo?.middleName ? values.patientInfo.middleName.split(' ').filter(m => m.length > 0) : []);

      const getStateCode = (state, country) => {
        if(!state) return null
        if(!country) return state
        try{
          if(country === 'US' || country === 'USA' || country === 'United States' || country === 'United States of America'){
            return CountryRegionData
              .find(country => {
                return country[1] === 'US';
              })[2]
              .split("|")
              .find(st => st.split("~")[0] === state).split("~")[1];
          }else{
    
            return CountryRegionData
              .find(c => {
                return c[0] === country;
              })[2]
              .split("|")
              .find(st => st.split("~")[0] === state).split("~")[1];
          }
        }catch {
          return state
        }
      }

      const addressLine = [
        values?.patientInfo?.address?.address,
        values?.patientInfo?.address?.address2
      ].filter(Boolean).join(" ");

      const params = {
        zipCode: values?.patientInfo?.address?.zipCode,
        radius: 15,
        organizationName: null,
        patientDemographics: {
          familyName: values?.patientInfo?.lastName,
          givenName: givenName,
          gender: convertToCareQualityGender(values?.patientInfo?.gender),
          // birthDate: values?.patientInfo?.dob,
          // birthDate: new Date(values?.patientInfo?.dob).toISOString().split('T')[0],
          birthDate: birthDate,
          addressLine: addressLine,
          city: values?.patientInfo?.address?.city,
          state: getStateCode(values?.patientInfo?.address?.state, values?.patientInfo?.address?.country),
          postalCode: values?.patientInfo?.address?.zipCode,
          country: values?.patientInfo?.address?.country
        } 
      }
      jwtPostFetcher(jwt)(`/api/carequality/queryForDocuments?requestId=${requestId}`, params)
    }
    
    return isRefPhysician ? navigate(`/${urlType}/records`) :  navigate(`/${urlType}/status`)
  }

  const onFailure = responseFailure((data, {status}) => {
    if(captchaEnabled){
      window.grecaptcha.reset()
    }
    if (status === 401) {
      return {
        '': "An error has occurred, please try again."
      }
    }
    if (status === 404) {
      return {
        '': "An error has occurred, please try again."
      }
    }
    if(status === 466) {
      console.log('466 Too many')
      return {
        '': "An error has occurred, please try again. (CODE: 466)"
      }
    }
  })

  const serviceTerms = useToggle()

  useEffect(() => {
    ensuredFormRef.current.clearErrors()
  },[serviceTerms.isOpen])

  const submitAndSaveTerms = ({signature, skipSignature}, locale) => {
    return action({...ensuredFormRef.current.getTransformedValues(),acceptedTerms: !skipSignature, ...(!skipSignature ? {serviceTermsInfo:  {signature,timezoneOffset:getTimezoneOffset(),locale}} : {})})
      .then((response) =>
        onSuccess(
          ensuredFormRef.current.getTransformedValues(),
          response
        )
      )
  }

  const withServiceTerms = providerInfo.requiresAgreement && providerInfo.serviceTerms && !isNurse && !isRefPhysician
  const [dismissed] = useUrgentNoticeDismissed()

  const checkAndOpenPreview = async () => {
    await ensuredFormRef.current.clearErrors()
    await ensuredFormRef.current.validate()
    
    if(!ensuredFormRef.current.hasErrors()){
      setIsPreview(true)
    }else{
      setNotification({ type: "alert", msg: 
        intl.formatMessage({
          defaultMessage: "Please check every step for incomplete or invalid fields",
          description: "Patient create request error message check every step"
        })
      })
      setRefresh(refresh + 1)
      return
    }
  }

  // if(isLoadingDefaultForm){
  //   return <LoadingDiv />
  // }

  return <>
    <Fill name="Navbar-Action">
      {isMultiLanguage && <LocaleSelector providerInfo={providerInfo}/>}
      <LogoutButton onClick={discardRequest}>Discard request</LogoutButton>
    </Fill>
    <PageContainer>
      <Header
        title="Expert opinion request"
      />
      {
        serviceTerms.isOpen &&
        <ServiceTermsContent
          isPending={true}
          onFailure={onFailure}
          action={(values) => submitAndSaveTerms(values, locale)}
          withNonPatientSkip
          withRecaptchaVerification={captchaEnabled}
          returnButton={<Button onClick={() =>{ 
              serviceTerms.close()
              setIsSubmitting(false)
          }}><FormattedMessage defaultMessage="Return to request details" description="Return to request details button" />
          </Button>}
        />
      }
      {isLoadingDefaultForm && <LoadingPage />}
      <div hidden={serviceTerms.isOpen || isLoadingDefaultForm}>
        <PanelStepsAndForm>
          <StepsWrapper ref={stepsWrapperRef} refresh={refresh}>
            {steps.map((step, index) => { 
              
              const title = step?.type === TAB_TYPE_CUSTOM 
                ? isMultiLanguage && step?.translations?.find(t => t.language === locale)?.name 
                  ? step?.translations?.find(t => t.language === locale)?.name
                  : step?.name
                : getTranslatedTitles(step?.id, intl)

              return <Step
                key={index} step={step} stepNumber={index + 1} 
                onClick={() => setSelectedStepNumber(step.order)}
                selected={selectedStepNumber === step.order}
                hasErrors={step.formPath ? ensuredFormRef?.current?.hasErrors(step?.formPath) : false}
              >
                {title}
                {step.formPath && ensuredFormRef?.current?.hasErrors(step.formPath) && 
                  <HasErrorsLabel>
                    <FormattedMessage defaultMessage="Review fields" description="Patient Create Request Review fields" />
                  </HasErrorsLabel>
                }
              </Step>
            })}
          </StepsWrapper>
          <PatientAndContactDetailsNew
              isNurse={isNurse}
              isRefPhysician={isRefPhysician}
              formRef={ensuredFormRef}
              onChange={onChange}
              onFailure={onFailure}
              action={withServiceTerms ? serviceTerms.open : ((values) => action(values)
                .then((response) => onSuccess(values, response))
              )}
              isAdmin={false}
              hideErrors={true}
              isPatientCreating={true}
              steps={steps}
              setSelectedStepNumber={setSelectedStepNumber}
              selectedStepNumber={selectedStepNumber}
              categories={categories}
              markedAsPriority={markedAsPriority}
          >{form => <>
            <PageFooter>
              {!withServiceTerms && captchaEnabled &&
              <RecaptchaBrandingText>
                This site is protected by reCAPTCHA and the Google <a
                href="https://policies.google.com/privacy"
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy Policy</a> and <a
                href="https://policies.google.com/terms"
                rel="noopener noreferrer"
                target="_blank"
              >Terms of Service</a> apply.
              </RecaptchaBrandingText>
              }
              {
                withServiceTerms
                  ? <Button highlight type="button" onClick={checkAndOpenPreview}><FormattedMessage defaultMessage="Submit" description="Patient Create Request Submit" /></Button>
                  : <Button highlight isLoading={form.isLoading} type="button" onClick={checkAndOpenPreview}><FormattedMessage defaultMessage="Submit" description="Patient Create Request Submit" /></Button>
              }

            </PageFooter>
          </>}</PatientAndContactDetailsNew>
        </PanelStepsAndForm>
        {isPreview && <Dialog
            // title="Case preview"
            title={intl.formatMessage({ defaultMessage: 'Case preview', description: 'Patient Create Request Case preview title' })}
            closeDialog={() => setIsPreview(false)}  
            footer={<>
              <Button onClick={() => setIsPreview(false)}><FormattedMessage defaultMessage="Return and edit" description="Patient Create Request Return and edit" /></Button>
              {withServiceTerms 
                ? 
                  <Button highlight isLoading={isSubmitting} onClick={() => {
                    setIsSubmitting(true)
                    ensuredFormRef.current.willSubmitForm()
                    setTimeout(() => setIsPreview(false), 200)
                  }}><FormattedMessage defaultMessage="Next" description="Patient Create Request Next" /></Button>
                 
                : <Button 
                    highlight 
                    isLoading={isSubmitting}
                    onClick={() => {
                      setIsSubmitting(true)
                      ensuredFormRef.current.willSubmitForm()
                      setTimeout(() => setIsPreview(false), 200)
                    }}
                ><FormattedMessage defaultMessage="Submit" description="Patient Create Request Submit" /></Button>
              } 
            </>}
          >
            <CasePreview>
              <div>
                {/* {isLegal ? <h4>Client identification</h4>: <h4>Patient identification</h4>} */}
                <h4>{intl.formatMessage({ defaultMessage: 'Patient identification', description: 'Patient Create Request Patient identification' })}</h4>
                <p>{ensuredFormRef.current.getValue('patientInfo.firstName')+' '}{ensuredFormRef.current.getValue('patientInfo.middleName') && ensuredFormRef.current.getValue('patientInfo.middleName')+' '}{ensuredFormRef.current.getValue('patientInfo.lastName') + ' - '}
                  {ensuredFormRef.current.getValue('patientInfo.dob') && defaultReformatDate(intl, "MM-dd-yyyy",new Date(ensuredFormRef.current.getValue('patientInfo.dob'))
                    .toISOString()
                    .split('T')[0]
                    .split('-')
                    .slice(1)
                    .concat([new Date(ensuredFormRef.current.getValue('patientInfo.dob')).toISOString().split('T')[0].split('-')[0]])
                    .join('-'))
                  } - {GENDER_LABELS(intl)[ensuredFormRef.current.getValue('patientInfo.gender')]}
                </p>
                <p>{ensuredFormRef.current.getValue('patientInfo.address.address')+' - '}{ensuredFormRef.current.getValue('patientInfo.address.address2') ? ensuredFormRef.current.getValue('patientInfo.address.address2') +' - ': ''}{ensuredFormRef.current.getValue('patientInfo.address.city')+', '}{ensuredFormRef.current.getValue('patientInfo.address.state')+' '}{ensuredFormRef.current.getValue('patientInfo.address.zipCode')} </p>
                <p>{ensuredFormRef.current.getValue('patientInfo.address.country')}</p>
              </div>
              <div>
                <h4>{intl.formatMessage({ defaultMessage: 'Contact party', description: 'Contact Party title' })}</h4>
                <p>{ensuredFormRef.current.getValue('contactDetails.contactRelationType') === PATIENT
                  ? ensuredFormRef.current.getValue('patientInfo.firstName')+' '+(ensuredFormRef.current.getValue('patientInfo.middleName') ? ensuredFormRef.current.getValue('patientInfo.middleName')+' ' : '')+ensuredFormRef.current.getValue('patientInfo.lastName')
                  : ensuredFormRef.current.getValue('contactDetails.fullName.first')+' '+ensuredFormRef.current.getValue('contactDetails.fullName.last')
                } ({getRelationLabel(intl, ensuredFormRef.current.getValue('contactDetails.contactRelationType'), ensuredFormRef.current.getValue('contactDetails.otherRelationType'))})</p>
                <p>{ensuredFormRef.current.getValue('contactDetails.phoneNumber')} - {ensuredFormRef.current.getValue('contactDetails.email')}</p>
                {/* <p>Preferred mode of communication: {ensuredFormRef.current.getValue('contactDetails.prefCommunicationMode').toLowerCase()} {ensuredFormRef.current.getValue('contactDetails.prefContactTime') ? <>, during the {ensuredFormRef.current.getValue('contactDetails.prefContactTime').toLowerCase()}.</> : ''}</p> */}
                <p><FormattedMessage
                  defaultMessage="Preferred mode of communication: {mode}{time, select, undefined {} other {, during the {time}.}}"
                  description="Patient Create Request Preferred mode of communication"
                  values={{
                    mode: ensuredFormRef.current.getValue('contactDetails.prefCommunicationMode').toLowerCase(),
                    time: ensuredFormRef.current.getValue('contactDetails.prefContactTime')?.toLowerCase()
                  }}
                /></p>
              </div>
              {!isLegal && steps.findIndex(step => step.label === 'Physician') !== -1 && ensuredFormRef.current.getTransformedValues()?.referringPhysician && (ensuredFormRef.current.getTransformedValues()?.referringPhysician?.fullName?.first || ensuredFormRef.current.getTransformedValues()?.referringPhysician?.fullName?.last) && <div>
                <h4><FormattedMessage defaultMessage="Referring physician" description="Contact relation label: Referring physician" /></h4>
                <p>
                  {ensuredFormRef.current.getTransformedValues()?.referringPhysician?.fullName?.first ? ensuredFormRef.current.getTransformedValues()?.referringPhysician?.fullName?.first + ' ' : ''}
                  {ensuredFormRef.current.getTransformedValues()?.referringPhysician?.fullName?.last ? ensuredFormRef.current.getTransformedValues()?.referringPhysician?.fullName?.last : ''}</p>
                <p>
                  {ensuredFormRef.current.getTransformedValues()?.referringPhysician?.phoneNumber 
                    ? ensuredFormRef.current.getTransformedValues()?.referringPhysician?.phoneNumber
                    : ensuredFormRef.current.getTransformedValues()?.referringPhysician?.phoneNumber
                  } 
                  {ensuredFormRef.current.getTransformedValues()?.referringPhysician?.email 
                    ? ensuredFormRef.current.getTransformedValues()?.referringPhysician?.phoneNumber 
                      ? ' - ' + ensuredFormRef.current.getTransformedValues()?.referringPhysician?.email 
                      : ensuredFormRef.current.getTransformedValues()?.referringPhysician?.email
                    : ''
                  }
                </p>
                {ensuredFormRef.current.getTransformedValues()?.referringPhysician?.institution && <p>Institution: {ensuredFormRef.current.getTransformedValues()?.referringPhysician?.institution}</p>}
              </div>}
              {!isLegal && steps.findIndex(step => step.label === 'Physician') !== -1 && ensuredFormRef.current.getTransformedValues()?.localPhysician && (ensuredFormRef.current.getTransformedValues()?.localPhysician?.fullName?.first || ensuredFormRef.current.getTransformedValues()?.localPhysician?.fullName?.last) && <div>
                <h4><FormattedMessage defaultMessage="Local physician" description="Contact relation label: Local physician" /></h4>
                <p>
                  {ensuredFormRef.current.getTransformedValues()?.localPhysician?.fullName?.first ? ensuredFormRef.current.getTransformedValues()?.localPhysician?.fullName?.first + ' ' : ''}
                  {ensuredFormRef.current.getTransformedValues()?.localPhysician?.fullName?.last ? ensuredFormRef.current.getTransformedValues()?.localPhysician?.fullName?.last : ''}
                </p>
                <p>
                  {ensuredFormRef.current.getTransformedValues()?.localPhysician?.phoneNumber 
                    ? ensuredFormRef.current.getTransformedValues()?.localPhysician?.phoneNumber
                    : ensuredFormRef.current.getTransformedValues()?.localPhysician?.phoneNumber
                  } 
                  {ensuredFormRef.current.getTransformedValues()?.localPhysician?.email 
                    ? ensuredFormRef.current.getTransformedValues()?.localPhysician?.phoneNumber 
                      ? ' - ' + ensuredFormRef.current.getTransformedValues()?.localPhysician?.email 
                      : ensuredFormRef.current.getTransformedValues()?.localPhysician?.email
                    : ''
                  }
                </p>
                {ensuredFormRef.current.getTransformedValues()?.localPhysician?.institution && <p>Institution: {ensuredFormRef.current.getTransformedValues()?.localPhysician?.institution}</p>}
              </div>}
              {ensuredFormRef.current.getValue('caseInfo.clinicalSummary') && <div>
                <IfLocale not contains="LEGAL">
                  <h4><FormattedMessage defaultMessage="Primary concern" description="Patient CaseInfo primary concern title" /></h4>
                  {ensuredFormRef.current.getValue('caseInfo.intakeDiagnosis') && <p> <FormattedMessage defaultMessage="Primary diagnosis:" description="RequestOverview primary diagnosis label" /> {ensuredFormRef.current.getValue('caseInfo.intakeDiagnosis')}</p>}
                  {ensuredFormRef.current.getValue('caseInfo.hasBeenTreatedOrSurgeryBefore') 
                    ? <p> <FormattedMessage defaultMessage="Patient has been treated or had surgery for this condition before:" description="Patient Create Request has been treated or had surgery before label" /> {ensuredFormRef.current.getValue('caseInfo.treatedOrSurgeryBeforeDescription')}</p>
                    : <p> <FormattedMessage defaultMessage="Patient has not been treated or had surgery for this condition before" description="Patient Create Request has not been treated or had surgery before label" /></p>
                  }
                  {ensuredFormRef.current.getValue('caseInfo.clinicalSummary') && <p> <FormattedMessage defaultMessage="Request description:" description="RequestOverview request description label" /> {ensuredFormRef.current.getValue('caseInfo.clinicalSummary')}</p>}
                </IfLocale>
                <IfLocale contains="LEGAL">
                  <h4>Case details</h4>
                  {ensuredFormRef.current.getValue('caseInfo.clinicalSummary') && <p>{ensuredFormRef.current.getValue('caseInfo.clinicalSummary')}</p>}
                </IfLocale>
              </div>}
            </CasePreview>
          </Dialog>}
      </div>
      <HideRecaptha/>
      <div
        ref={captchaRef}
        className="g-recaptcha"
      />
    </PageContainer>
    <PurviewFooter/>
  </>
}

const AddCategories = ({}) => {
  const categories = lazyPublicCategories.read()
  if (!categories.length) {
    return null
  }
  return <><SectionHeading>
    <h3>Case divisions <OptionalHeading>(Optional)</OptionalHeading></h3>
    <p>You can include division(s) so that specific division users would be able to access this case.</p>
  </SectionHeading>
    <Card css="margin-bottom: 1rem;">
      <CategoriesBox lazyCategories={lazyPublicCategories} autoFocus={false}/>
    </Card>
  </>
}
